if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.sectionId = (this.id || '').replace('MediaGallery-', '');

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          sliderList: this.querySelector(`#Slider-Gallery-${this.sectionId}`),
          thumbList: this.querySelector(`#Slider-Thumbnails-${this.sectionId}`),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 200));

        // Thumb clicks
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (!btn) return;
            btn.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        // ✅ Robust filtering + dots
        this.initColorFiltering();
        this.initMobileDots();
      }

      /* ---------------------------
        HELPERS (ALWAYS FRESH DOM)
      ---------------------------- */

      getVariantSelects() {
        return document.querySelector(`#variant-selects-${this.sectionId}`);
      }

      norm(s) {
        return (s || '')
          .toString()
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-')
          .replace(/_/g, '-')
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss');
      }

      // ✅ “Braun vs brown / Schwarz vs black” usw. robust machen
      getColorKeys(selectedColorRaw) {
        const n = this.norm(selectedColorRaw);
        const map = {
          schwarz: ['black'],
          black: ['schwarz'],
          braun: ['brown'],
          brown: ['braun'],
          nude: ['nude'],
          beige: ['nude', 'beige'],
          sand: ['nude', 'beige'],
        };

        const keys = new Set([n]);
        (map[n] || []).forEach((k) => keys.add(this.norm(k)));
        return Array.from(keys);
      }

      getSelectedColorValue() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return null;

        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
        const colorFieldset = fieldsets.find((fs) => {
          const legend = fs.querySelector('legend');
          const txt = (legend?.textContent || '').toLowerCase();
          return txt.includes('farbe') || txt.includes('color');
        });

        if (colorFieldset) {
          const checked = colorFieldset.querySelector('input[type="radio"]:checked');
          if (checked?.value) return checked.value.trim();
        }

        // fallback
        const checkedByName =
          variantSelects.querySelector('input[type="radio"][name*="Farbe"]:checked') ||
          variantSelects.querySelector('input[type="radio"][name*="Color"]:checked');
        if (checkedByName?.value) return checkedByName.value.trim();

        return null;
      }

      getSlidesAndThumbs() {
        const slides = this.elements.sliderList
          ? Array.from(this.elements.sliderList.querySelectorAll('li[data-media-id]'))
          : [];
        const thumbs = this.elements.thumbList
          ? Array.from(this.elements.thumbList.querySelectorAll('li[data-target]'))
          : [];
        return { slides, thumbs };
      }

      // Keep original order using Liquid-provided data-estrell-pos
      sortByPos(a, b) {
        const ap = parseInt(a?.dataset?.estrellPos || '9999', 10);
        const bp = parseInt(b?.dataset?.estrellPos || '9999', 10);
        return ap - bp;
      }

      // ✅ Match-Logik: erst data-color, dann fallback auf Bild-URL (wenn data-color mal leer ist)
      slideMatchesColor(li, colorKeys) {
        const dc = this.norm(li?.dataset?.color);
        if (dc && colorKeys.includes(dc)) return true;

        // fallback: versuche über Bild-URL
        const img = li.querySelector('img');
        const src = this.norm(img?.currentSrc || img?.src || '');
        if (!src) return false;

        return colorKeys.some((k) => src.includes(k));
      }

      thumbMatchesColor(li, colorKeys) {
        const dc = this.norm(li?.dataset?.color);
        if (dc && colorKeys.includes(dc)) return true;

        const img = li.querySelector('img');
        const src = this.norm(img?.currentSrc || img?.src || '');
        if (!src) return false;

        return colorKeys.some((k) => src.includes(k));
      }

      // ✅ Dawn-Component cached sliderItems → nach DOM-Änderungen neu “syncen”
      syncSliderItems() {
        try {
          if (this.elements.viewer) {
            this.elements.viewer.sliderItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
          }
          if (this.elements.thumbnails) {
            this.elements.thumbnails.sliderItems = Array.from(
              this.elements.thumbnails.querySelectorAll('li[data-target]')
            );
          }
        } catch (_) { }
      }

      /* ---------------------------
        COLOR FILTERING (NO MIXING)
      ---------------------------- */

      initColorFiltering() {
        let lastSelectedColor = null;

        const run = (data) => {
          const newColor = this.getSelectedColorValue();

          // Only refilter and rebuild if the color actually changed
          if (lastSelectedColor !== null && lastSelectedColor === newColor) {
            return;
          }
          lastSelectedColor = newColor;

          this.filterMediaToSelectedColor(true);
          this.rebuildMobileDots?.();
        };

        // Initial
        window.setTimeout(run, 0);

        // Subscribe to variant changes via PubSub
        if (typeof subscribe === 'function') {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            window.setTimeout(() => run(event.data), 0);
          });

          subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            window.setTimeout(() => run(event.data), 0);
          });
        }

        // Dawn / custom events (covers section re-render)
        document.addEventListener('shopify:section:load', () => window.setTimeout(run, 0));
      }

      showAllMedia() {
        // Use all stored slides & thumbs so we never lose them.
        // We initialize this.allSlides and this.allThumbs if missing.
        if (!this.allSlides) {
          const { slides, thumbs } = this.getSlidesAndThumbs();
          this.allSlides = slides;
          this.allThumbs = thumbs;
        }

        const slides = this.allSlides;
        const thumbs = this.allThumbs;

        // Clear display: none
        slides.sort(this.sortByPos).forEach((li) => {
          li.hidden = false;
          li.classList.remove('is-active');
          li.style.display = '';
          if (this.elements.sliderList) this.elements.sliderList.appendChild(li);
        });

        thumbs.sort(this.sortByPos).forEach((li) => {
          li.hidden = false;
          li.style.display = '';
          if (this.elements.thumbList) this.elements.thumbList.appendChild(li);
        });

        this.syncSliderItems();

        // activate first slide
        const first = slides.find((li) => li.style.display !== 'none');

        // Wait for next paint so Dawn's `clientWidth > 0` checks pass when re-calculating offsets.
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (first?.dataset?.mediaId) this.setActiveMedia(first.dataset.mediaId, false);

            this.elements.viewer?.resetPages?.();
            this.elements.thumbnails?.resetPages?.();
            this.rebuildMobileDots?.();

            // ✅ Force immediate scroll to start after everything is shown
            if (this.elements.viewer?.slider) this.elements.viewer.slider.scrollLeft = 0;
          }, 50);
        });
      }

      filterMediaToSelectedColor(activateFirstVisible = false) {
        console.log('MediaGallery: filtering media. Selected color:', this.getSelectedColorValue());
        const selectedColor = this.getSelectedColorValue();
        if (!selectedColor) {
          this.showAllMedia();
          return;
        }

        const colorKeys = this.getColorKeys(selectedColor);

        // Ensure we always filter from the full list, not just what's currently in DOM
        if (!this.allSlides) {
          const { slides, thumbs } = this.getSlidesAndThumbs();
          this.allSlides = slides;
          this.allThumbs = thumbs;
        }

        const slides = this.allSlides;
        const thumbs = this.allThumbs;
        if (!slides.length) return;

        // matches by data-color OR image url fallback
        const matches = slides.filter((li) => this.slideMatchesColor(li, colorKeys));

        // Wenn gar nichts matcht → UX nicht kaputt machen
        if (!matches.length) {
          this.showAllMedia();
          return;
        }

        const visibleSlides = [];
        // Apply display:none instead of detaching so global.js holds onto nodes
        slides.forEach((li) => {
          li.classList.remove('is-active');
          if (this.slideMatchesColor(li, colorKeys)) {
            li.style.display = ''; // Make visible
            visibleSlides.push(li);
          } else {
            li.style.display = 'none'; // Hide physically to fix gaps
          }
        });

        // re-sort visible slides if required based on data-estrell-pos
        visibleSlides.sort(this.sortByPos).forEach(li => this.elements.sliderList.appendChild(li))

        thumbs.forEach((li) => {
          if (this.thumbMatchesColor(li, colorKeys)) {
            li.style.display = '';
          } else {
            li.style.display = 'none';
          }
        });

        // re-sort thumbs
        thumbs.filter(li => li.style.display !== 'none').sort(this.sortByPos).forEach(li => {
          if (this.elements.thumbList) this.elements.thumbList.appendChild(li);
        });

        // ✅ Synchronous Scroll Reset (Before Dawn's logic kicks in)
        if (this.elements.viewer?.slider) {
          this.elements.viewer.slider.style.scrollBehavior = 'auto'; // Disable smooth scroll temporarily
          this.elements.viewer.slider.scrollLeft = 0;
        }

        this.syncSliderItems();

        // Ensure active slide is visible and reset scroll to start
        const firstVisible = visibleSlides[0];

        const ensureStart = () => {
          try {
            // ✅ Instant scroll to avoid "sliding" feel during correction
            this.elements.viewer?.slider?.scrollTo({ left: 0, behavior: 'auto' });
          } catch (_) { }
        };

        // Wait for next paint so Dawn's `clientWidth > 0` checks pass when re-calculating offsets.
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (activateFirstVisible && firstVisible?.dataset?.mediaId) {
              this.setActiveMedia(firstVisible.dataset.mediaId, false);
              ensureStart();
            } else {
              const active = this.elements.sliderList?.querySelector('li.is-active[data-media-id]');
              if (!active || active.style.display === 'none') {
                if (firstVisible?.dataset?.mediaId) this.setActiveMedia(firstVisible.dataset.mediaId, false);
                ensureStart();
              }
            }

            // Recalc slider pages
            this.elements.viewer?.resetPages?.();
            this.elements.thumbnails?.resetPages?.();

            // Fix aria-current to first visible thumb
            if (this.elements.thumbnails) {
              this.elements.thumbnails
                .querySelectorAll('button[aria-current]')
                .forEach((btn) => btn.removeAttribute('aria-current'));
              const firstThumbBtn = this.elements.thumbnails.querySelector('li[data-target]:not([style*="display: none"]) button');
              if (firstThumbBtn) firstThumbBtn.setAttribute('aria-current', 'true');
            }

            this.rebuildMobileDots?.();

            // ✅ Restore scroll behavior if needed (optional, behavior auto is safer)
            if (this.elements.viewer?.slider) {
              this.elements.viewer.slider.style.scrollBehavior = '';
            }
          }, 50);
        });
      }

      /* ---------------------------
        MOBILE DOTS (BASED ON VISIBLE SLIDES)
      ---------------------------- */

      initMobileDots() {
        const dotsWrap = this.querySelector('[data-mobile-dots]');
        if (!dotsWrap) return;

        const getVisibleSlides = () => {
          if (!this.elements.sliderList) return [];
          // Dots should only map to slides that aren't display:none
          return Array.from(this.elements.sliderList.querySelectorAll('li[data-media-id]')).filter((li) => li.style.display !== 'none');
        };

        const getLabel = (idx) => {
          return `Bild ${idx + 1}`;
        };

        const rebuild = () => {
          if (window.matchMedia('(min-width: 750px)').matches) {
            dotsWrap.innerHTML = '';
            return;
          }

          const slides = getVisibleSlides();
          dotsWrap.innerHTML = '';

          slides.forEach((slide, idx) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('aria-label', getLabel(idx));
            b.addEventListener('click', () => {
              const id = slide.dataset.mediaId;
              if (id) this.setActiveMedia(id, false);
            });
            dotsWrap.appendChild(b);
          });

          const activeEl = this.elements.sliderList?.querySelector('li.is-active[data-media-id]') || slides[0];
          const activeId = activeEl?.dataset?.mediaId;
          const activeIdx = activeId ? slides.findIndex((s) => s.dataset.mediaId === activeId) : 0;

          Array.from(dotsWrap.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === activeIdx);
          });
        };

        this.rebuildMobileDots = rebuild;
        rebuild();

        this.elements.viewer?.addEventListener('slideChanged', (event) => {
          if (window.matchMedia('(min-width: 750px)').matches) return;

          const currentEl = event?.detail?.currentElement;
          const currentId = currentEl?.dataset?.mediaId;

          const slides = getVisibleSlides();
          const idx = currentId ? slides.findIndex((s) => s.dataset.mediaId === currentId) : -1;

          Array.from(dotsWrap.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === idx);
          });
        });

        // ✅ Fallback for native mobile swiping
        if (this.elements.sliderList) {
          this.elements.sliderList.addEventListener('scroll', () => {
            if (window.matchMedia('(min-width: 750px)').matches) return;

            const slides = getVisibleSlides();
            if (!slides.length) return;

            // Calculate which slide is currently most visible based on scroll horizontal position
            const scrollLeft = this.elements.sliderList.scrollLeft;
            const slideWidth = slides[0].clientWidth || slides[0].offsetWidth;

            if (slideWidth > 0) {
              const idx = Math.round(scrollLeft / slideWidth);
              Array.from(dotsWrap.children).forEach((dot, i) => {
                dot.classList.toggle('is-active', i === idx);
              });
            }
          }, { passive: true });
        }

        // ✅ gedrosselt
        window.addEventListener('resize', debounce(rebuild, 150));
      }

      /* ---------------------------
        DAWN BASE METHODS
      ---------------------------- */

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${CSS.escape(event.detail.currentElement.dataset.mediaId)}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // ✅ Anti-Double-Switch (früh)
        const currentActive = this.elements.viewer.querySelector('li.is-active[data-media-id]');
        if (currentActive?.dataset?.mediaId === mediaId) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;
        if (activeMedia.hidden) return;
        if (activeMedia.classList.contains('is-active')) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        // prepend bleibt drin (Original-Dawn Feature)
        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${CSS.escape(mediaId)}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.rebuildMobileDots?.();
          return;
        }

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${CSS.escape(mediaId)}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        this.rebuildMobileDots?.();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail || thumbnail.hidden) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}